import setDynamicFormFieldValue from "@bevco/Store/Handlers/DynamicForm/SetFieldValue";
import { CustomState } from "@bevco/Store/Reducers";
import { DynamicFormContext } from "@bevco/Widgets/DynamicForm/DynamicForm";
import DynamicFormFieldShell from "@bevco/Widgets/DynamicForm/DynamicFormFieldShell";
import { useRegisterDynamicFormFieldKeys } from "@bevco/Widgets/DynamicForm/useRegisterDynamicFormFieldKeys";
import siteMessage from "@insite/client-framework/SiteMessage";
import { CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Select from "@insite/mobius/Select";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    fieldKey = "fieldKey",
    countryFieldKey = "countryFieldKey",
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.fieldKey]: string;
        [fields.countryFieldKey]: string;
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

/** Maps Dynamic Form Redux state into widget props. */
const mapStateToProps = (state: CustomState, ownProps: OwnProps) => {
    const fieldKey = ownProps.fields.fieldKey || "State";
    const countryFieldKey = ownProps.fields.countryFieldKey || "Country";
    const countryValue = state.customDynamicForm?.fieldValues[countryFieldKey];

    let states: StateModel[] = [];
    if (countryValue) {
        const countries = (state.customCountriesList?.countries as CountryModel[]) || [];
        const countrySelected = countries.find(country => country.name === countryValue);
        if (countrySelected) {
            states = countrySelected.states ?? [];
        }
    }

    return {
        fieldKey,
        countryFieldKey,
        countryValue,
        fieldValue: state.customDynamicForm?.fieldValues[fieldKey],
        states,
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const widgetGroup = "SalesForce Dynamic" as any;

/** Salesforce Dynamic Form state/province field widget. */
const DynamicState: React.FC<Props> = ({
    id,
    fields: widgetFields,
    fieldKey,
    countryValue,
    fieldValue,
    states,
    setDynamicFormFieldValue,
}) => {
    const { label, hintText, isRequired } = widgetFields;
    const formContext = React.useContext(DynamicFormContext);
    const [errorMessage, setErrorMessage] = React.useState("");
    useRegisterDynamicFormFieldKeys(id, [fieldKey]);

    // Clear state when country changes and the current value is no longer valid.
    React.useEffect(() => {
        if (!fieldValue) {
            return;
        }
        const stillValid = states.some(s => s.name === fieldValue);
        if (!stillValid) {
            setDynamicFormFieldValue({ key: fieldKey, value: "" });
        }
    }, [countryValue, states, fieldValue, fieldKey, setDynamicFormFieldValue]);

    const validate = React.useCallback(() => {
        const error = isRequired && !fieldValue ? (siteMessage("Field_Required", label) as string) : "";
        setErrorMessage(error);
        return !error;
    }, [fieldValue, isRequired, label]);

    React.useEffect(() => {
        formContext.validators[fieldKey] = validate;
        if (typeof fieldValue !== "undefined") {
            validate();
        }
        return () => {
            formContext.validators[fieldKey] = undefined;
        };
    }, [fieldKey, fieldValue, validate, formContext.validators]);

    return (
        <DynamicFormFieldShell>
            <Select
                label={label}
                required={isRequired}
                value={fieldValue || ""}
                error={errorMessage}
                onChange={event => setDynamicFormFieldValue({ key: fieldKey, value: event.currentTarget.value })}
            >
                <option value="">{hintText}</option>
                {states.map(stateOption => (
                    <option key={stateOption.id} value={stateOption.name}>
                        {stateOption.name}
                    </option>
                ))}
            </Select>
        </DynamicFormFieldShell>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(DynamicState),
    definition: {
        group: widgetGroup,
        displayName: "State",
        icon: "map",
        fieldDefinitions: [
            {
                name: fields.fieldKey,
                displayName: "Salesforce Field (API Name)",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.countryFieldKey,
                displayName: "Country Salesforce Field (API Name)",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
